import React, { ReactNode } from 'react';
import Checkbox from '@/components/common/libraries/common/Checkbox';
import Collapse from '@/components/common/libraries/common/Collapse';
import Filter from '@/components/common/libraries/common/Filter';

interface LessonLike {
  label: ReactNode;
  value: string | number;
  checked?: boolean;
}

interface UnitLike {
  label: string;
  lessons: LessonLike[];
}

interface Props<U extends UnitLike> {
  onLessonChange: (value: string | number, checked: boolean) => void;
  onUnitChange: (unit: U, checked: boolean) => void;
  outerClassName?: string;
  shouldCollapseUnit?: (unit: U) => boolean;
  title: ReactNode;
  units: U[];
}

// AIDEV-NOTE: Shared "Unit → Lessons" cascade for library FilterBars.
// Outer Collapse wraps the section; each unit gets a nested Collapse
// whose trigger is a Checkbox (toggling all child lessons) and whose
// children are flat Filter rows for each lesson.
const UnitFilterSection = <U extends UnitLike>({
  onLessonChange,
  onUnitChange,
  outerClassName,
  shouldCollapseUnit,
  title,
  units,
}: Props<U>) => (
  <div className={outerClassName}>
    <Collapse triggerElement={<>{title}</>}>
      {units.map((unitFilter) => {
        const allLessonsChecked = unitFilter.lessons.every(lesson => lesson.checked);
        const someLessonsChecked = unitFilter.lessons.some(lesson => lesson.checked);
        const collapseUnit = shouldCollapseUnit
          ? shouldCollapseUnit(unitFilter)
          : unitFilter.lessons.every(lesson => !lesson.checked);

        return (
          <div key={unitFilter.label}>
            <Collapse
              caret="row"
              shouldCollapse={collapseUnit}
              triggerElement={(
                <Checkbox
                  checked={allLessonsChecked}
                  extraClassName="mb5"
                  indeterminate={someLessonsChecked && !allLessonsChecked}
                  label={unitFilter.label}
                  onChange={(event) => {
                    onUnitChange(unitFilter, event.target.checked);
                  }}
                />
              )}
            >
              {unitFilter.lessons.map(lesson => (
                <Filter
                  key={lesson.value}
                  checked={lesson.checked ?? false}
                  className="tw-pl-4"
                  label={lesson.label}
                  value={lesson.value}
                  onChange={onLessonChange}
                />
              ))}
            </Collapse>
          </div>
        );
      })}
    </Collapse>
  </div>
);

export default UnitFilterSection;
